import React, { useEffect } from 'react';
import Intercom from '@intercom/messenger-js-sdk';
import { decodeToken } from 'react-jwt';
import { apiGetUserInfo } from '@/services/UserService';

interface IntercomProviderProps {
    children: React.ReactNode;
}

interface DecodedToken {
    id?: string;
    userId?: string;
    sub?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    iat?: number;
    exp?: number;
}

interface UserInfo {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    token: string;
    endPoint: string;
    subscriptionName: string;
    subscriptionStatus: string;
    dueDate: string;
}

const IntercomProvider: React.FC<IntercomProviderProps> = ({ children }) => {
    useEffect(() => {
        const initializeIntercom = async () => {

            const token = localStorage.getItem("accessToken");

            if (token) {
                try {
                    const decodedToken = decodeToken(token) as DecodedToken;

                    if (decodedToken) {
                        const userId = decodedToken.id || decodedToken.userId || decodedToken.sub || 'unknown';

                        try {
                            // Fetch user info from API to get complete user data
                            const userInfoResponse = await apiGetUserInfo();
                            const userInfo = userInfoResponse.data as UserInfo;

                            const userName = userInfo.firstName && userInfo.lastName
                                ? `${userInfo.firstName} ${userInfo.lastName}`
                                : userInfo.firstName || userInfo.username || 'User';
                            const userEmail = userInfo.username || ''; // username is typically the email
                            const createdAt = decodedToken.iat || Math.floor(Date.now() / 1000);

                            const intercomConfig = {
                                app_id: 'rq48bo5h',
                                user_id: userId,
                                name: userName,
                                email: userEmail,
                                created_at: createdAt,
                                // Additional custom attributes
                                subscription_status: userInfo.subscriptionStatus || 'NO_SUBSCRIPTION',
                                subscription_name: userInfo.subscriptionName || 'Free',
                                user_hash: undefined, // Add this if you implement secure mode
                            };




                            // Initialize Intercom
                            Intercom(intercomConfig);

                            // Verify Intercom is available
                            if (window.Intercom) {

                                // Update with user data
                                window.Intercom('update', intercomConfig);
                            } else {
                                console.warn('⚠️ IntercomProvider: window.Intercom is not available after initialization');
                            }

                        } catch (apiError) {

                            // Fallback to token data only
                            const userName = 'User';
                            const userEmail = '';
                            const createdAt = decodedToken.iat || Math.floor(Date.now() / 1000);

                            const fallbackConfig = {
                                app_id: 'rq48bo5h',
                                user_id: userId,
                                name: userName,
                                email: userEmail,
                                created_at: createdAt,
                            };

                             Intercom(fallbackConfig);
                        }
                    } else {
                        console.error('❌ IntercomProvider: Failed to decode token - decoded token is null/undefined');
                    }
                } catch (error) {
                    console.error('💥 IntercomProvider: Failed to decode token:', error);
                }
            } else {
                console.log('🔍 IntercomProvider: Available localStorage keys:', Object.keys(localStorage));
            }
        };

        initializeIntercom();

        // Cleanup function to shutdown Intercom when component unmounts
        return () => {
             if (window.Intercom) {
                window.Intercom('shutdown');
            } else {
                console.warn('⚠️ IntercomProvider: window.Intercom not available for shutdown');
            }
        };
    }, []);

    return <>{children}</>;
};

export default IntercomProvider;